import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { CreditCard, Zap, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  application_fee: { fr: "Frais de dossier (75€)", en: "Application fee (€75)" },
  enrollment_deposit: { fr: "Acompte d'inscription (295€/390€)", en: "Enrollment deposit (€295/€390)" },
  us_history_deposit: { fr: "Acompte US History", en: "US History deposit" },
  balance_installment: { fr: "Mensualité solde", en: "Balance installment" },
};

export default function AdminPendingPayments() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/pending-payments")
      .then(r => setItems(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const simulate = async (sessionId, name) => {
    if (!window.confirm(lang === "fr"
      ? `Simuler le paiement de "${name}" comme réussi ?\n\nCela contourne Stripe (utile à cause du bug Emergent sandbox).`
      : `Simulate "${name}"'s payment as successful?\n\nThis bypasses Stripe (workaround for the Emergent sandbox bug).`)) return;
    setPaying(sessionId);
    try {
      await api.post(`/admin/simulate-payment/${sessionId}`);
      toast.success(lang === "fr" ? `Paiement simulé pour ${name}` : `Payment simulated for ${name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(null);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

  return (
    <div data-testid="admin-pending-payments">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-serif text-3xl tracking-tight mb-2">
        {lang === "fr" ? "Paiements en attente · Déblocage admin" : "Pending payments · Admin override"}
      </h1>

      <div className="mb-6 rounded-xl border border-primary/30 bg-primary/[0.04] p-4 flex items-start gap-3" data-testid="sandbox-warning">
        <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold text-primary mb-1">
            {lang === "fr" ? "Outil de déblocage manuel" : "Manual unblock tool"}
          </div>
          <p className="text-primary/80 leading-relaxed">
            {lang === "fr"
              ? "Stripe est en mode LIVE. Cette page liste les sessions Checkout initiées qui n'ont pas (encore) abouti — utile pour les paiements SEPA en attente de mandat ou pour débloquer une famille bloquée sur Stripe. Le bouton « Simuler le paiement » est réservé aux super admins, journalisé, et déclenche les mêmes transitions qu'un paiement Stripe réussi (à utiliser uniquement pour un paiement reçu hors-ligne ou en cas de problème confirmé)."
              : "Stripe is in LIVE mode. This page lists initiated Checkout sessions that have not (yet) completed — useful for SEPA payments awaiting mandate or to unblock a family stuck on Stripe. The « Simulate payment » button is super-admin only, audit-logged, and triggers the same transitions as a successful Stripe payment (use only for offline payments received or confirmed issues)."}
          </p>
        </div>
      </div>

      <div className="mb-4 flex justify-end">
        <Button onClick={load} variant="outline" size="sm" data-testid="refresh-pending">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          {lang === "fr" ? "Actualiser" : "Refresh"}
        </Button>
      </div>

      {loading && <div className="text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          {lang === "fr" ? "Aucun paiement en attente." : "No pending payments."}
        </CardContent></Card>
      )}

      {!loading && items.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">{lang === "fr" ? "Élève" : "Student"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Famille" : "Family"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Type" : "Type"}</th>
                  <th className="p-3 text-right">{lang === "fr" ? "Montant" : "Amount"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Statut" : "Status"}</th>
                  <th className="p-3 text-left">Session</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(tx => (
                  <tr key={tx.id} data-testid={`pending-row-${tx.id}`} className="border-t border-border hover:bg-secondary/20">
                    <td className="p-3 font-medium">{tx.student_name || "—"}</td>
                    <td className="p-3 text-xs">{tx.family_email || "—"}</td>
                    <td className="p-3 text-xs">
                      <Badge variant="outline">{KIND_LABEL[tx.kind]?.[lang] || tx.kind}</Badge>
                    </td>
                    <td className="p-3 text-right tabular-nums font-display font-semibold">{fmt(tx.amount)} €</td>
                    <td className="p-3"><Badge>{tx.payment_status || "pending"}</Badge></td>
                    <td className="p-3 text-[10px] font-mono text-muted-foreground truncate max-w-[140px]">{tx.session_id}</td>
                    <td className="p-3 text-right">
                      <Button
                        onClick={() => simulate(tx.session_id, tx.student_name || tx.session_id.slice(0, 8))}
                        disabled={paying === tx.session_id}
                        size="sm"
                        className="bg-accent text-accent-foreground hover:bg-accent/90"
                        data-testid={`simulate-${tx.id}`}
                      >
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                        {paying === tx.session_id
                          ? (lang === "fr" ? "Simulation…" : "Simulating…")
                          : (lang === "fr" ? "Simuler" : "Simulate")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
